/**
 * Results panel (av-methodology-matrix-ui spec; av-methodology-classification
 * spec's "methodological chain" requirement; design D18, D26).
 *
 * Visual structure reproduces the historical prototype's result blocks
 * (`docs/art-direction/metodologia-av/index.html` `#result0`/`#resultbox`):
 * a "Conteúdo indicado" heading followed by three columns — "Utilize:" /
 * "Sugestões de recursos sonoros:" / "Veja possibilidade de hibridismo:" —
 * though every word of content in them is sourced from Metodologia AV 2.0 and
 * the JSON data, not the historical placeholder "texto texto texto".
 *
 * Selection header (name/color/position) — previously a separate
 * below-the-table toolbar panel — is now folded in here, since that toolbar
 * was removed per the author's "não usar mais botões abaixo da tabela."
 *
 * Design D26: every content gets its own full block (header + "Conteúdo
 * indicado" + 3 columns), stacked one after another — not just the
 * currently-selected one. The author reported having to click each content
 * individually to see it, one at a time; the panel now lists all of them so
 * nothing is hidden behind a selection. `selectedContentId` only decides
 * which block gets a subtle highlight — it never hides any other block.
 *
 * Design D28: the "Veja possibilidade de hibridismo" column used to stop at
 * the resulting position's coluna/linha — a number, not a meaning. It now
 * also shows what that result *means* (hybridization-interpretation.ts):
 * a generic, position-derived interpretation (always present, tagged
 * "derived-from-position") plus the thesis's one documented worked example
 * when the result matches it (posição 10 — tagged ferreira-2010, never
 * presented as if it applied to every arrival at that position).
 *
 * Design D33: the content's name, shown in each block's own header, is
 * click-to-rename — the author reported double-clicking the matrix square
 * (the only prior way to rename) "não deu certo" (two ordinary clicks,
 * outside the browser's dblclick timing window, register as nothing —
 * a discoverability failure, not a code bug; the same root cause the
 * influence-weight edit glyph fixed earlier). Enter commits and refreshes
 * the matrix too (the SVG label under the square reads the same name);
 * Escape cancels.
 */

import { classify } from "../domain/classification";
import { calculateContentHybridization } from "../domain/hybridization";
import { interpretHybridizationResult, resolveHybridizationPosition } from "../domain/hybridization-interpretation";
import { referenceLanguageForArchetype } from "../domain/reference-language";
import { getRecommendation, type RecommendationResult } from "../domain/recommendation-engine";
import type { ContentNode, InfluenceNode } from "../domain/types";
import { loadInfluenceNodes } from "../state/content-store";

export type RenameContentHandler = (contentId: string, newLabel: string) => void;

export function renderResults(
  container: HTMLElement,
  contents: ContentNode[],
  selectedContentId: string | null = null,
  onRenameContent?: RenameContentHandler,
): void {
  container.innerHTML = "";

  if (contents.length === 0) {
    const heading = document.createElement("h2");
    heading.textContent = "Resultado";
    const empty = document.createElement("p");
    empty.textContent = "Clique em uma célula da matriz para criar um conteúdo, ou selecione um existente.";
    container.append(heading, empty);
    return;
  }

  for (const content of contents) {
    container.appendChild(renderContentEntry(content, content.id === selectedContentId, onRenameContent));
  }
}

function renderContentEntry(
  content: ContentNode,
  isSelected: boolean,
  onRenameContent?: RenameContentHandler,
): HTMLElement {
  const classified = classify(content);
  const recommendation = getRecommendation(content);
  // Design D29: reference-language label sourced from the interpretation
  // layer's own catalog, not archetypes.json directly — classify().archetype
  // stays as a defensive fallback only (the two are asserted identical by
  // data-shape.test.ts).
  const referenceLanguageLabel = referenceLanguageForArchetype(content.archetypeId)?.label ?? classified.archetype.label;

  const entry = document.createElement("div");
  entry.className = "results-entry";
  if (isSelected) entry.classList.add("is-selected");

  const header = document.createElement("div");
  header.className = "results-header";
  header.innerHTML = `
    <span class="color-swatch" style="background:${escapeAttr(content.color)}"></span>
    <strong class="content-name" tabindex="0" role="button" aria-label="Editar nome de ${escapeAttr(content.label)}">${escapeHtml(content.label)}</strong>
    <span class="position-badge">${escapeHtml(referenceLanguageLabel)} / ${escapeHtml(classified.relevanceLevel.label)}</span>
    <span class="position-badge">peso ${content.weight}</span>
  `;
  const nameEl = header.querySelector<HTMLElement>(".content-name");
  if (nameEl && onRenameContent) {
    attachInlineRename(nameEl, content, onRenameContent);
  }
  entry.appendChild(header);

  const heading = document.createElement("h3");
  heading.textContent = "Conteúdo indicado";
  entry.appendChild(heading);

  if (recommendation.status === "not-recorded") {
    const notice = document.createElement("p");
    notice.textContent = recommendation.message;
    entry.appendChild(notice);
    return entry;
  }

  const resultbox = document.createElement("ul");
  resultbox.className = "resultbox";

  // "Utilize:" — METODOLOGIA: posição → características → indicação funcional.
  const utilize = document.createElement("li");
  utilize.innerHTML = `
    <h5>Utilize:</h5>
    <p>${escapeHtml(recommendation.methodologicalStatement)}</p>
    <p class="muted">${escapeHtml(recommendation.historical.elementos)}</p>
  `;
  resultbox.appendChild(utilize);

  // "Sugestões de recursos sonoros:" — CATÁLOGO TECNOLÓGICO, histórico e atual distinguíveis.
  const sonoros = document.createElement("li");
  const parts: string[] = [];
  if (recommendation.historical.audio || recommendation.historical.video) {
    parts.push(`
      <span class="provenance-tag historical">2010</span>
      ${recommendation.historical.audio ? `<p>Áudio: ${recommendation.historical.audio.join(", ")}</p>` : ""}
      ${recommendation.historical.video ? `<p>Vídeo: ${recommendation.historical.video.join(", ")}</p>` : ""}
    `);
  }
  if (recommendation.current) {
    const optionsList = recommendation.current.preferredOptions
      .map((o) => `${escapeHtml(o.label)} (${escapeHtml(o.status)})`)
      .join(", ");
    parts.push(`
      <span class="provenance-tag current">2026</span>
      <p>${optionsList || "—"}</p>
      ${recommendation.current.message ? `<p class="muted">${escapeHtml(recommendation.current.message)}</p>` : ""}
    `);
  }

  // Design D31/D32: the hybridization result's own 2026 suggestion (when
  // there is one) lives HERE — "Sugestões de recursos sonoros" — not inside
  // "Veja possibilidade de hibridismo", per the author's report that it was
  // confusingly duplicated/misplaced there. Captioned so it isn't mistaken
  // for the content's OWN position's suggestion above, when the two differ.
  // When they're the SAME suggestion (the 2026 rule engine is fact-based,
  // not position-specific, so this happens often), hybridizationParts()
  // itself suppresses the second block entirely — never show the same
  // codec list twice (design D32).
  const hybridization = hybridizationParts(content, recommendation);
  if (hybridization.currentSuggestionHtml) {
    parts.push(`
      <p class="muted"><strong>Para a posição resultante do hibridismo:</strong></p>
      ${hybridization.currentSuggestionHtml}
    `);
  }

  sonoros.innerHTML = `<h5>Sugestões de recursos sonoros:</h5>${parts.join("") || "<p>—</p>"}`;
  resultbox.appendChild(sonoros);

  // "Veja possibilidade de hibridismo:" — Metodologia AV 2.0 operational result.
  const hibridismo = document.createElement("li");
  hibridismo.innerHTML = `<h5>Veja possibilidade de hibridismo:</h5>${hybridization.hibridismoHtml}`;
  resultbox.appendChild(hibridismo);

  entry.appendChild(resultbox);
  return entry;
}

interface HybridizationParts {
  /** Goes inside "Veja possibilidade de hibridismo": the resulting
   *  position, its interpretation, and the ferreira-2010 worked example
   *  when one is documented for that position — never a codec/technology
   *  suggestion (design D31: that belongs in "Sugestões de recursos
   *  sonoros" instead, not duplicated here). */
  hibridismoHtml: string;
  /** Goes inside "Sugestões de recursos sonoros" instead — the result
   *  position's own review-2026 technology suggestion, when it has one AND
   *  it differs from the content's own base-position suggestion (design
   *  D32: an identical suggestion is shown once, not twice). Empty string
   *  when there's nothing to show (no influences yet, no current
   *  recommendation for the result position, or it's a duplicate of the
   *  content's own). */
  currentSuggestionHtml: string;
}

/** Stable key for comparing two `preferredOptions` lists — codec ids, in
 *  order, ignoring label/status text. Used only to detect when the result
 *  position's 2026 suggestion is the same as the content's own (design D32:
 *  the rule engine is fact-based, not position-specific, so this is common
 *  — showing the same list twice would just be noise). */

/**
 * Click (or Enter/Space when focused) the content's name to rename it
 * in place — Enter commits, Escape cancels (design D33). Committing calls
 * `onRename`, which the caller wires to trigger a full save + rerender
 * (including the matrix, whose SVG label shows the same name) — this
 * function does not update the matrix itself, and does not need to restore
 * `nameEl` on commit either, since the resulting rerender replaces this
 * entire block. It DOES need to restore it on cancel, since nothing
 * triggers a rerender in that case.
 */
function attachInlineRename(nameEl: HTMLElement, content: ContentNode, onRename: RenameContentHandler): void {
  const startEdit = (): void => {
    const input = document.createElement("input");
    input.type = "text";
    input.className = "content-name-input";
    input.value = content.label;
    input.setAttribute("aria-label", "Nome do conteúdo");
    nameEl.replaceWith(input);
    input.focus();
    input.select();

    let settled = false;
    const commit = (): void => {
      if (settled) return;
      settled = true;
      const newLabel = input.value.trim();
      onRename(content.id, newLabel || content.label);
    };
    const cancel = (): void => {
      if (settled) return;
      settled = true;
      input.replaceWith(nameEl);
    };

    input.addEventListener("keydown", (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        commit();
      } else if (event.key === "Escape") {
        event.preventDefault();
        cancel();
      }
    });
    input.addEventListener("blur", commit);
  };

  nameEl.addEventListener("click", startEdit);
  nameEl.addEventListener("keydown", (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      startEdit();
    }
  });
}

function optionsKey(options: Array<{ id: string; status: string }>): string {
  return options.map((o) => `${o.id}:${o.status}`).join("|");
}

function hybridizationParts(content: ContentNode, ownRecommendation: RecommendationResult): HybridizationParts {
  const influences: InfluenceNode[] = loadInfluenceNodes(content.id);
  if (influences.length === 0) {
    return {
      hibridismoHtml: `<p class="muted">Sem influências ainda. Selecione este conteúdo e use o "+" na matriz para adicionar uma.</p>`,
      currentSuggestionHtml: "",
    };
  }

  const inputs = influences.map((influence) => {
    const c = classify(influence);
    return { order: c.archetype.order, relevanceValue: c.relevanceLevel.value, weight: influence.weight };
  });
  const result = calculateContentHybridization(inputs);
  const resultPosition = resolveHybridizationPosition(result.coluna, result.linha);

  if (!resultPosition) {
    // Defensive only — coluna/linha are always in-range by construction
    // (see resolveHybridizationPosition's own doc comment).
    return {
      hibridismoHtml: `<p>Posição resultante: coluna ${result.coluna}, linha ${result.linha} — a partir de ${influences.length} influência(s).</p>`,
      currentSuggestionHtml: "",
    };
  }

  const interpreted = interpretHybridizationResult(content, resultPosition);

  const historicalBlock = interpreted.historicalExample
    ? `
      <div class="hybridization-example">
        <span class="provenance-tag historical">2010</span>
        <p>${escapeHtml(interpreted.historicalExample.interpretation)}</p>
        <p>${escapeHtml(interpreted.historicalExample.suggestion)}</p>
        <p class="muted">${escapeHtml(interpreted.historicalExample.context)} — ${escapeHtml(interpreted.historicalExample.source)}</p>
      </div>
    `
    : "";

  const resultCurrent =
    interpreted.recommendation.status === "recorded" ? interpreted.recommendation.current : null;
  const ownCurrent = ownRecommendation.status === "recorded" ? ownRecommendation.current : null;
  // Design D32: don't show the result position's suggestion if it's
  // identical to the content's own — same codecs, same statuses, same
  // order. A genuinely different suggestion still shows (captioned) at the
  // call site in renderContentEntry().
  const isDuplicateOfOwn =
    resultCurrent && ownCurrent && optionsKey(resultCurrent.preferredOptions) === optionsKey(ownCurrent.preferredOptions);

  const currentSuggestionHtml =
    resultCurrent && !isDuplicateOfOwn
      ? `
        <div class="hybridization-example">
          <span class="provenance-tag current">2026</span>
          <p>${resultCurrent.preferredOptions.map((o) => `${escapeHtml(o.label)} (${escapeHtml(o.status)})`).join(", ") || "—"}</p>
        </div>
      `
      : "";

  const hibridismoHtml = `
    <span class="provenance-tag current">Metodologia AV 2.0</span>
    <p>Posição resultante: coluna ${result.coluna} (${escapeHtml(interpreted.resultReferenceLanguageLabel)}),
    linha ${result.linha} (${escapeHtml(interpreted.resultRelevanceLabel)})
    — a partir de ${influences.length} influência(s).</p>

    <p>${escapeHtml(interpreted.interpretation)}
    <span class="provenance-tag pending">${escapeHtml(interpretationOriginLabel(interpreted.interpretationOrigin))}</span></p>
    <p class="muted">Características estendidas: ${interpreted.extendedCharacteristics.map(escapeHtml).join(", ")}</p>

    ${historicalBlock}

    <p class="muted">A fórmula original de 2010 permanece documentada como registro histórico ambíguo,
    nunca executada — ver Origem científica.</p>
  `;

  return { hibridismoHtml, currentSuggestionHtml };
}

/**
 * UI-only translation (design D30) — `interpreted.interpretationOrigin`'s
 * raw value ("derived-from-position") stays in English internally, since
 * it's a stable provenance tag read by tests and other code
 * (hybridization-interpretation.ts); this maps it to Portuguese display
 * text only where it's shown to the reader, the same way "historical"/
 * "current" are shown as "2010"/"2026" rather than their raw class names.
 */
function interpretationOriginLabel(origin: string): string {
  return origin === "derived-from-position" ? "derivado de posição" : origin;
}

function escapeHtml(value: string): string {
  const div = document.createElement("div");
  div.textContent = value;
  return div.innerHTML;
}

function escapeAttr(value: string): string {
  return value.replace(/"/g, "&quot;");
}
